/**
 * Catalogue des widgets des tableaux de bord personnalisés.
 * Tailles en unités de grille (12 colonnes, lignes de 40 px) ; min = limite de lisibilité.
 * scope : multi = agrège les pages du tableau de bord, single = une page choisie, none = indépendant des pages.
 * Doit rester aligné avec la liste blanche de l'engine (src/models/schemas/dashboard.js).
 */
export const CATEGORIES = ['performance', 'reporting', 'publications', 'operations', 'other']

// [clé, composant, icône, fit (sans liste : hauteur ajustée au contenu), réseaux concernés, une seule page]
const REPORTING_PANELS = [
  ['reporting_overview', 'ReportingOverview', 'assessment', true],
  ['reporting_visibility', 'ReportingVisibility', 'visibility', true],
  ['reporting_impressions', 'ReportingImpressions', 'remove_red_eye', true],
  ['reporting_engagement', 'ReportingEngagement', 'favorite', true],
  ['reporting_content', 'ReportingContent', 'view_list', false],
  ['reporting_post_types', 'ReportingPostTypes', 'pie_chart', true],
  ['reporting_videos', 'ReportingVideos', 'videocam', false],
  ['reporting_stories', 'ReportingStories', 'filter_none', false, ['facebook', 'instagram']],
  ['reporting_demographics', 'ReportingDemographics', 'public', true],
  ['reporting_concurrent', 'ReportingConcurrent', 'compare_arrows', false, null, true]
]

export const WIDGETS = [
  // Performance (pages du tableau de bord agrégées)
  { key: 'metric', fit: true, category: 'performance', icon: 'show_chart', scope: 'multi', needs: ['metrics'], reporting: true, w: 4, h: 5, minW: 2, minH: 3, maxH: 14,
    options: { keys: ['impressions'], viz: 'tile', compare: true, network: null, top: 5 } },
  { key: 'kpis', ownCard: true, fit: true, category: 'performance', icon: 'dashboard', scope: 'multi', needs: ['overview'], w: 12, h: 4, minW: 3, minH: 3, maxH: 10,
    options: { tiles: null } },
  { key: 'chart', ownCard: true, category: 'performance', icon: 'timeline', scope: 'multi', needs: ['overview'], reporting: true, w: 6, h: 8, minW: 4, minH: 6, maxH: 16 },
  { key: 'networks', ownCard: true, category: 'performance', icon: 'device_hub', scope: 'multi', needs: ['overview', 'pages'], reporting: true, w: 12, h: 7, minW: 6, minH: 4, maxH: 20 },
  { key: 'best_times', ownCard: true, category: 'performance', icon: 'access_time', scope: 'multi', needs: ['overview'], reporting: true, w: 4, h: 8, minW: 4, minH: 7, maxH: 12 },
  // Clics des liens courts mws.li (feature beta url_shortener) ; les données viennent de /reporting/short-links
  { key: 'short_links', fit: true, category: 'performance', icon: 'touch_app', scope: 'multi', needs: [], shortLinks: true, w: 6, h: 6, minW: 3, minH: 3, maxH: 16,
    options: { viz: 'tile', groupBy: 'page', top: 5 } },
  { key: 'calendar', category: 'performance', icon: 'event', scope: 'multi', needs: ['overview'], reporting: true, w: 8, h: 6, minW: 6, minH: 5, maxH: 10 },

  // Reporting détaillé (une page au choix, mêmes panneaux que la section Reporting)
  // networks : réseaux concernés (absent = tous les réseaux du Reporting)
  ...REPORTING_PANELS.map(([key, component, icon, fit, networks, singlePage]) => ({
    key, component, networks, fit, singlePage: !!singlePage, category: 'reporting', icon, scope: 'single', needs: ['pageStats'], reporting: true, w: 12, h: fit ? 8 : 12, minW: 6, minH: fit ? 3 : 6, maxH: 30,
    options: { pageIds: [] }
  })),

  // Publications (tous réseaux, via la solution)
  { key: 'distribution', category: 'publications', icon: 'donut_large', scope: 'multi', needs: ['overview'], w: 4, h: 7, minW: 3, minH: 5, maxH: 12 },
  { key: 'last_posts', ownCard: true, category: 'publications', icon: 'history', scope: 'multi', needs: ['overview', 'pages'], w: 6, h: 10, minW: 4, minH: 6, maxH: 24 },
  { key: 'top_posts', ownCard: true, category: 'publications', icon: 'star', scope: 'multi', needs: ['overview'], reporting: true, w: 8, h: 8, minW: 4, minH: 6, maxH: 14 },
  { key: 'upcoming', ownCard: true, category: 'publications', icon: 'schedule', scope: 'multi', needs: ['pages', 'posts'], w: 6, h: 10, minW: 4, minH: 6, maxH: 24 },

  // Opérationnel
  { key: 'alerts', ownCard: true, fit: true, category: 'operations', icon: 'notifications_active', scope: 'multi', needs: ['pages', 'posts', 'inbox'], w: 12, h: 1, minW: 6, minH: 1, maxH: 4 },
  { key: 'pages', ownCard: true, category: 'operations', icon: 'pages', scope: 'multi', needs: ['pages', 'overview', 'posts', 'inbox'], w: 6, h: 10, minW: 4, minH: 6, maxH: 24 },
  { key: 'inbox', category: 'operations', icon: 'question_answer', scope: 'multi', needs: ['pages', 'inbox'], inbox: true, w: 6, h: 8, minW: 4, minH: 5, maxH: 20 },
  { key: 'sources', ownCard: true, category: 'operations', icon: 'rss_feed', scope: 'none', needs: [], w: 6, h: 10, minW: 4, minH: 6, maxH: 24 },
  { key: 'presence', ownCard: true, category: 'operations', icon: 'location_on', scope: 'none', needs: [], presence: true, w: 12, h: 9, minW: 6, minH: 6, maxH: 20 },

  // Autre
  { key: 'note', fit: true, category: 'other', icon: 'short_text', scope: 'none', needs: [], w: 4, h: 3, minW: 2, minH: 1, maxH: 60,
    options: { text: '', align: 'left', size: 'm', background: null, color: null } },
  { key: 'actions', fit: true, category: 'other', icon: 'touch_app', scope: 'none', needs: ['inbox'], w: 12, h: 1, minW: 2, minH: 1, maxH: 6,
    options: { actions: ['create', 'planner', 'inbox', 'reporting'], layout: 'row', hideTitle: true } }
]

export const WIDGETS_BY_KEY = WIDGETS.reduce((map, w) => ({ ...map, [w.key]: w }), {})

// Tableau de bord de départ proposé à la création
export const STARTER_LAYOUT = [
  { module: 'kpis', x: 0, y: 0, w: 12, h: 4 },
  { module: 'last_posts', x: 0, y: 4, w: 6, h: 10 },
  { module: 'top_posts', x: 6, y: 4, w: 6, h: 10 },
  { module: 'best_times', x: 0, y: 14, w: 4, h: 8 },
  { module: 'chart', x: 4, y: 14, w: 8, h: 8 }
]

let counter = 0
export const newItemId = () => `w${Date.now().toString(36)}${(counter++).toString(36)}`

// Nouvel élément de layout pour un widget du catalogue (options par défaut copiées)
export function createItem(key, extra = {}) {
  const def = WIDGETS_BY_KEY[key]
  return {
    i: newItemId(),
    module: key,
    x: 0,
    y: 0,
    w: def.w,
    h: def.h,
    options: JSON.parse(JSON.stringify(def.options || {})),
    ...extra
  }
}
